import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";

import { StatusCode } from "api/protocols";
import { TAccountingReference } from "redux/reducers/models/facility";
import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import { toAccountingReferenceGroupSaveValue, toAccountingReferenceGroupSelectValue } from "helpers/accountingReferences";
import Page from "components/page/Page";
import Popup from "components/popup/Popup";
import SelectNew from "components/select/SelectNew";

import AccountingReferenceFields, { TAccountingReferenceFieldsState } from "./AccountingReferenceFields";
import { TAccountingReferenceApi } from "./accountingReferenceApi.types";

type AccountingReferenceEditProps = {
  api: TAccountingReferenceApi;
};

export default function AccountingReferenceEdit({ api }: AccountingReferenceEditProps) {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const [accRefState, setAccRefState] = useState<TAccountingReferenceFieldsState>({
    title: "",
    reference_number: "",
    group: "none",
  });
  const [savedAccRefState, setSavedAccRefState] = useState<TAccountingReferenceFieldsState>(undefined);
  const [allAccountingReferences, setAllAccountingReferences] = useState<TAccountingReference[]>([]);
  const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);
  const [migrateAccountingReference, setMigrateAccountingReference] = useState<number>(undefined);

  useEffect(() => {
    void loadAccountingReferences();
  }, [id]);

  async function loadAccountingReferences() {
    const res = await api.GetAccountingReference(null, true);

    if (res.status !== StatusCode.OK) {
      return;
    }

    const currentRef = res.data.find(accRef => accRef.id === Number(id));

    if (!currentRef) {
      dispatch(showError(t("Accounting reference does not exist")));
      history.push("/admin/settings/accounting_references");
      return;
    }

    const loadedState: TAccountingReferenceFieldsState = {
      title: currentRef.title,
      reference_number: currentRef.reference_number,
      group: toAccountingReferenceGroupSelectValue(currentRef.group),
    };

    setAllAccountingReferences(res.data);
    setAccRefState(loadedState);
    setSavedAccRefState(loadedState);
  }

  async function saveAccountingReference() {
    const res = await api.PutAccountingReference(
      { id, ...accRefState, group: toAccountingReferenceGroupSaveValue(accRefState.group) },
      true,
    );

    if (res.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/accounting_references");
  }

  async function removeAccountingReference() {
    const res = await api.DeleteAccountingReference(
      { id, migrate_accounting_reference_id: migrateAccountingReference },
      true,
    );

    if (res.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/accounting_references");
  }

  function handleDeleteModalVisibility() {
    setDeleteModalVisibility(prev => !prev);
  }

  const migrateOptions = allAccountingReferences
    .filter(accRef => accRef.id !== Number(id))
    .map(accRef => ({
      label: accRef.reference_number ? `${accRef.title} • ${accRef.reference_number}` : accRef.title,
      id: accRef.id,
    }));

  return (
    <div>
      <Page
        title={t("Edit Accounting Reference")}
        narrow
        primaryAction={{ content: t("Save"), action: saveAccountingReference }}
        secondaryActions={[{ content: t("Delete"), action: handleDeleteModalVisibility }]}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Accounting References"),
            url: "/admin/settings/accounting_references",
          },
        ]}
        notificationBarProps={{
          isVisible: savedAccRefState !== undefined && !isEqual(savedAccRefState, accRefState),
          onAction: saveAccountingReference,
          onCancel: () => setAccRefState(savedAccRefState),
        }}
      >
        <AccountingReferenceFields value={accRefState} onChange={setAccRefState} />
      </Page>

      <Popup
        open={deleteModalVisibility}
        type="warning"
        title={t("Deletion Confirmation")}
        description={t("Are you sure you want to delete this accounting reference?")}
        onOk={removeAccountingReference}
        okText={t("Delete")}
        onCancel={handleDeleteModalVisibility}
        backDropCancel={true}
      >
        <SelectNew
          label={t("Accounting Reference Type")}
          id="accounting_reference_type"
          options={migrateOptions}
          value={migrateAccountingReference}
          onChange={value => setMigrateAccountingReference(value)}
          searchable
          usePortal
        />
      </Popup>
    </div>
  );
}
